import { gql, NetworkStatus, useApolloClient, useQuery } from '@apollo/client'
import cx from 'classnames'
import PageComponent from 'components/Dashboard/PageComponent'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { Link } from 'gatsby'
import Pusher from 'pusher-js'
import { useEffect, useState } from 'react'
import { FaSync } from 'react-icons/fa'
import ConfirmPhotoModal from './ConfirmPhotoModal'
import { DashboardTitle } from './DashboardTitleContext'
import { resultHistoryFragment } from './ResultHistory'
import KitLink from './ResultQueue/KitLink'
import ResultRow, { ResultRowProps } from './ResultQueue/ResultRow'
import { UnmatchedResults, UnmatchedResultsVariables } from './__generated__/UnmatchedResults'

const QUERY = gql`
  query UnmatchedResults($first: Int!, $after: String, $queueName: String) {
    unmatchedResultsConnection(first: $first, after: $after, queueName: $queueName) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          status
          versionId
          queuedTime
          queueName
          escalatedAt
          escalatedBy
          claimedAt
          claimedBy
          lastReminderEmailAt
          lastReminderEmailBy
          isHandled @client
          labEvent {
            labId
            eventType
            firstName
            lastName
            dob
            rawData
            result
            ingestDetails {
              filename
              csvRow
              ingestDate
            }
            exceptionMessage
            comment
          }
          statedKit {
            id
            sku
            status
            antigenResult
            registrationInfo {
              firstName
              lastName
              dob
            }
          }
          ...ResultHistoryFragment
        }
        unmatchDetail
        contact {
          email
          emailType
        }
      }
    }
  }

  ${resultHistoryFragment}
`

const PhotoConfirmRow = ({ edge }: ResultRowProps) => {
  const result = edge.node
  const [showModal, setShowModal] = useState(false)

  return (
    <tr>
      <td>
        {edge.node.isHandled ? (
          'Already handled'
        ) : (
          <>
            <strong>Waiting: </strong>
            {formatDistanceToNow(parseISO(edge.node.queuedTime))}

            {edge.node.queuedTime !== edge.node.labEvent.ingestDetails.ingestDate && (
              <>
                {' '}
                (Last registered{' '}
                {formatDistanceToNow(parseISO(edge.node.labEvent.ingestDetails.ingestDate), { addSuffix: true })})
              </>
            )}
          </>
        )}
        <br />
        <KitLink kit={edge.node.statedKit} />
      </td>
      <td>
        Photo requires verification
        {edge.node.escalatedAt && (
          <>
            <br />
            <span className="has-text-danger">Escalated</span>{' '}
            {formatDistanceToNow(parseISO(edge.node.escalatedAt), {
              addSuffix: true,
            })}{' '}
            by {edge.node.escalatedBy}.
          </>
        )}
        {edge.node.claimedBy && (
          <>
            <br />
            <strong>Claimed by: </strong>
            {edge.node.claimedBy}
            {', '}
            {formatDistanceToNow(parseISO(edge.node.claimedAt), {
              addSuffix: true,
            })}
          </>
        )}
      </td>
      <td>
        <p>
          <button
            className={cx('button is-small is-info  is-fullwidth', {
              'is-outlined': edge.node.claimedAt,
            })}
            disabled={edge.node.isHandled}
            onClick={() => setShowModal(true)}
          >
            {edge.node.isHandled ? 'Already handled' : 'Verify photo...'}
          </button>
        </p>

        {showModal && (
          <ConfirmPhotoModal
            onClose={() => setShowModal(false)}
            kitId={edge.node.statedKit.id}
            sku={edge.node.statedKit.sku}
            antigenResult={edge.node.statedKit.antigenResult}
            versionId={edge.node.versionId}
            result={result}
            allowEscalate={!edge.node.escalatedAt}
          />
        )}
      </td>
    </tr>
  )
}

interface MatchedResultsProps {
  queueName?: string
  limit?: number
  titleFn: (qty: number, currentlyShownQty: number) => React.ReactNode
}

export const MatchedResults = ({ queueName, titleFn, limit = 500 }: MatchedResultsProps) => {
  const { error, data, refetch, fetchMore, networkStatus } = useQuery<UnmatchedResults, UnmatchedResultsVariables>(
    QUERY,
    {
      variables: { first: limit, queueName },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    }
  )

  const client = useApolloClient()

  useEffect(() => {
    const pusher = new Pusher(process.env.GATSBY_PUSHER_APP_KEY, {
      cluster: process.env.GATSBY_PUSHER_CLUSTER,
    })

    const channel = pusher.subscribe('dash')
    channel.bind('result-claim', function (data) {
      client.query({
        query: gql`
          query ResultClaimantQuery($resultId: ID!) {
            result(id: $resultId) {
              id
              claimedAt
              claimedBy
            }
          }
        `,
        variables: { resultId: data.id },
        fetchPolicy: 'network-only',
      })
    })

    channel.bind('result-handled', function (data) {
      client.writeFragment({
        id: `Result:${data.id}`,
        fragment: gql`
          fragment IsHandled on Result {
            isHandled @client
          }
        `,
        data: { isHandled: true },
      })
    })

    return () => pusher.unsubscribe('dash')
  }, [client])

  if (error) {
    return <p>ERROR {error.message}</p>
  }

  const qty = data?.unmatchedResultsConnection.totalCount || 0

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">
              {networkStatus === NetworkStatus.loading
                ? 'Loading...'
                : titleFn(qty, data?.unmatchedResultsConnection.edges.length || 0)}
            </p>
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus !== NetworkStatus.ready,
              })}
              disabled={networkStatus !== NetworkStatus.ready}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </nav>

      <table className="table is-bordered is-fullwidth">
        <thead>
          <tr>
            <th>Result Details</th>
            <th>Issue</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data?.unmatchedResultsConnection.edges
            .filter((r) => r.node.status !== 'DELETED')
            .map((r) => {
              if (r.node.status === 'PHOTO_NEEDS_CONFIRMATION') {
                return <PhotoConfirmRow key={r.cursor} edge={r} />
              }

              return <ResultRow key={r.cursor} edge={r} />
            })}
        </tbody>
      </table>

      {data?.unmatchedResultsConnection.pageInfo.hasNextPage && (
        <nav className="level has-width-unset">
          <div className="level-left" />

          <div className="level-right">
            <div className="level-item">
              <button
                className={cx('button is-info is-outlined', {
                  'is-loading': networkStatus !== NetworkStatus.ready,
                })}
                onClick={() =>
                  fetchMore({
                    variables: {
                      after: data.unmatchedResultsConnection.pageInfo.endCursor,
                    },
                  })
                }
              >
                Load More
              </button>
            </div>
          </div>
        </nav>
      )}
    </>
  )
}

const Results: PageComponent = () => (
  <div className="container">
    <DashboardTitle title="Results" />

    <MatchedResults
      titleFn={(qty, currentlyShowQty) => (
        <>
          <strong>{qty}</strong> results needing attention (displaying first {currentlyShowQty})
          <br />
          <Link className="is-size-6 has-text-weight-normal" to="/dashboard/archived-results">
            Archived Results
          </Link>
        </>
      )}
    />
  </div>
)

Results.path = '/results'
Results.displayName = 'Results'

export default Results
